import { Guard } from "./types";

export function checkExhausted(value: never): never {
  throw new Error(`Value not exhausted: ${JSON.stringify(value)}`);
}

export const ELLIPSISE = {
  overflowX: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  maxW: "100%",
} as const;

export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

export function isArrayOf<T>(guard: Guard<T>): Guard<Array<T>> {
  return (value): value is Array<T> =>
    Array.isArray(value) && value.every(guard);
}

export function replaceItem<T>(
  arr: Array<T>,
  index: number,
  item?: T | null
): Array<T> {
  const copy = [...arr];

  if (item != null) {
    copy.splice(index, 1, item);
  } else {
    copy.splice(index, 1);
  }

  return copy;
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

export function isRecordOf<K extends string, V>(
  keyGuard: Guard<K>,
  valueGuard: Guard<V>
): Guard<Record<K, V>> {
  return (value): value is Record<K, V> =>
    isObject(value) &&
    Object.entries(value).every(
      ([key, value]) => keyGuard(key) && valueGuard(value)
    );
}

export function hasKey<K extends string, V>(
  obj: Record<string, unknown>,
  key: K,
  guard: Guard<V>
): obj is { [k in K]: V } {
  return key in obj && guard(obj[key]);
}

export function hasOptionalKey<K extends string, V>(
  obj: Record<string, unknown>,
  key: K,
  guard: Guard<V>
): obj is { [k in K]?: V } {
  return !(key in obj) || guard(obj[key]) || obj[key] === undefined;
}

export function isOneOf<S extends string>(options: Array<S>): Guard<S> {
  return (value): value is S => options.some((other) => value === other);
}

export function findAndMap<I, O>(
  arr: Array<I>,
  mapper: (val: I, index: number) => O | null | undefined
): O | null {
  for (let i = 0; i < arr.length; i++) {
    const item = mapper(arr[i], i);

    if (item != null) {
      return item;
    }
  }
  return null;
}

export function filterAndMap<I, O>(
  arr: Array<I>,
  mapper: (val: I, index: number) => O | null | undefined
): Array<O> {
  const out: Array<O> = [];
  for (let i = 0; i < arr.length; i++) {
    const item = mapper(arr[i], i);

    if (item != null) {
      out.push(item);
    }
  }
  return out;
}

// Shallow defaults, with the further in the parameter list, then the higher priority
export function defaults<T extends object>(
  original: T,
  ...partials: Partial<T>[]
): T {
  const reversedPartials = partials.reverse();
  const out = { ...original };
  for (const key in original) {
    const value = findAndMap(reversedPartials, (partial) =>
      key in partial ? partial[key] : null
    );
    if (value != null) {
      original[key] = value;
    }
  }
  return out;
}

type Entry<O, K extends keyof O> = [K, O[K]];

export function typedEntries<O extends object>(obj: O): Entry<O, keyof O>[] {
  return Object.entries(obj) as Entry<O, keyof O>[];
}
